import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Collapse,
  FormControlLabel,
  Switch,
  Link,
  Tooltip,
  TextField,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RouterIcon from '@mui/icons-material/Router';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import VideocamIcon from '@mui/icons-material/Videocam';
import SensorsIcon from '@mui/icons-material/Sensors';
import HubIcon from '@mui/icons-material/Hub';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { api } from '../services/api';
import { auth } from '../services/auth';
import { getParentLatencyMs, formatLatencyMs } from '../services/latency';

import { MAP_UI } from '../theme/mapViewUi';

const JUGANU_YELLOW = MAP_UI.accentMuted;
const JUGANU_GREEN = MAP_UI.deviceOnlineBorder;
const JUGANU_RED = MAP_UI.deviceOfflineBorder;

/** Neutral chip for Overview device-type health (no green/yellow/red). */
const overviewStatusChipSx = {
  bgcolor: 'action.hover',
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'divider',
  fontWeight: 700,
  '& .MuiChip-icon': { ml: 0.75 },
};

const overviewStatusChipIconSx = {
  width: 5,
  height: 5,
  borderRadius: '50%',
  bgcolor: 'text.disabled',
};

const PROVISIONING_MISMATCH_REASON_LABELS = {
  not_in_provisioning: 'Not in provisioning cache',
  child_not_in_provisioning: 'Child not provisioned',
};

// normalizeMeshStaBand maps a device's uplink (STA) band string to a
// canonical bucket used on the Overview page. Treemgmt nodes operate
// on 5 GHz or 6 GHz backhaul; anything else (2.4 GHz) lands in
// "other". Missing band is handled by getMeshStaBandFromDevice.
const normalizeMeshStaBand = (rawBand) => {
  if (rawBand === null || rawBand === undefined) return 'offline';
  const s = String(rawBand).toLowerCase().replace(/\s+/g, '');
  if (s.includes('5ghz') || s === '5' || s === '2') return '5GHz';
  if (s.includes('6ghz') || s === '6' || s === '3') return '6GHz';
  if (s.includes('2.4') || s.includes('2ghz')) return 'other';
  if (s === '') return 'offline';
  return 'other';
};

const getMeshStaBandFromDevice = (device) => {
  if (!device) return 'offline';
  if (!device.connected) return 'offline';
  return normalizeMeshStaBand(device.ul_band);
};

const isMeshBackhaulNode = (capabilities) => getDeviceTypeFromCapabilities(capabilities) === 'Node';

// Shared device-type helper (also used inside the component).
function getDeviceTypeFromCapabilities(capabilities) {
  if (!capabilities) return 'N/A';
  if (typeof capabilities === 'object') {
    const model = capabilities.model;
    if (model) {
      const modelLower = model.toLowerCase();
      if (modelLower.includes('juganucap')) return 'CAP';
      if (modelLower.includes('juganunode')) return 'Node';
      if (modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb')) return 'AI';
      if (modelLower.includes('edgecore') || modelLower.includes('eap102')) return 'EdgeCore';
    }
    if (capabilities.compatible === 'edgecore_eap102') return 'EdgeCore';
  }
  return 'N/A';
}

const InstallationStatusTab = ({ isActive, devices = [] }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showTreeStaBand = auth.canWriteBackhaul();
  const sectionTwoGridMd = showTreeStaBand ? 4 : 6;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [provisioningMismatch, setProvisioningMismatch] = useState(null);
  const [expandedProvisioningMismatch, setExpandedProvisioningMismatch] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState({});
  const [expandedDeviceTypes, setExpandedDeviceTypes] = useState({});
  const [expandedNetworkQuality, setExpandedNetworkQuality] = useState({});
  const [networkQualityThresholds, setNetworkQualityThresholds] = useState({
    latency: null,
    packetLoss: null,
    connectedTimeAvg: null,
    connectedTimeMedian: null,
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);
  // Note: intentionally no global totals / summary state; UI is per-device-type only.

  const fetchNetworkStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, mismatchData] = await Promise.all([
        api.getProjectNetworkStatus(),
        api.getProvisioningMismatch(),
      ]);
      setNetworkData(data);
      setProvisioningMismatch(mismatchData);
    } catch (err) {
      console.error('Error fetching network status:', err);
      setError(err.message || 'Failed to fetch network status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isActive && !networkData) {
      fetchNetworkStatus();
    }
  }, [isActive, networkData, fetchNetworkStatus]);

  // Auto-refresh effect - runs every 2 minutes when enabled
  useEffect(() => {
    if (autoRefresh && isActive) {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      // Set up new interval for 2 minutes (120000ms)
      intervalRef.current = setInterval(() => {
        fetchNetworkStatus();
      }, 120000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      // Clear interval when auto-refresh is disabled or tab is inactive
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [autoRefresh, isActive, fetchNetworkStatus]);

  const handleRefresh = () => {
    fetchNetworkStatus();
  };

  const handleAutoRefreshToggle = (event) => {
    setAutoRefresh(event.target.checked);
  };

  const toggleVersionExpand = (deviceType) => {
    setExpandedVersions(prev => ({
      ...prev,
      [deviceType]: !prev[deviceType]
    }));
  };

  const toggleDeviceTypeExpand = (deviceType) => {
    setExpandedDeviceTypes(prev => ({
      ...prev,
      [deviceType]: !prev[deviceType]
    }));
  };

  // Get device type from device capabilities (matches DeviceList logic)
  const getDeviceType = (capabilities) => getDeviceTypeFromCapabilities(capabilities);

  // Map API device type names to getDeviceType values
  const mapApiDeviceTypeToDeviceType = (apiType) => {
    const typeLower = (apiType || '').toLowerCase();
    if (typeLower.includes('cap') || typeLower.includes('juganucap')) {
      return 'CAP';
    } else if (typeLower.includes('node') || typeLower.includes('juganunode')) {
      return 'Node';
    } else if (typeLower.includes('nvidia') || typeLower.includes('ai') || typeLower.includes('qrb5165')) {
      return 'AI';
    } else if (typeLower.includes('edgecore') || typeLower.includes('eap102')) {
      return 'EdgeCore';
    }
    return null;
  };

  // Filter offline devices by device type
  const getOfflineDevicesByType = (deviceType) => {
    const mappedType = mapApiDeviceTypeToDeviceType(deviceType);
    if (!mappedType) return [];

    return devices.filter(device => {
      const deviceTypeFromCap = getDeviceType(device.capabilities);
      return deviceTypeFromCap === mappedType && !device.connected;
    });
  };

  // Format relative time (e.g., "2h ago")
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = Date.now();
    const lastSeenTime = new Date(timestamp).getTime();
    const diff = (now - lastSeenTime) / 1000; // Convert to seconds
    
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // Network Quality drill-down functions
  const toggleNetworkQualityExpand = (metricKey) => {
    setExpandedNetworkQuality(prev => ({
      ...prev,
      [metricKey]: !prev[metricKey]
    }));
  };

  // Median round-trip latency to the device's parent in ms.
  // Returns null when the device hasn't reported usable ping data.
  const getDeviceParentLatencyMs = (device) => {
    const ms = getParentLatencyMs(device);
    return ms !== null && ms > 0 ? ms : null;
  };

  // Filter connected devices whose parent latency exceeds the threshold (ms).
  // Higher ms = worse, so we surface anything ABOVE the threshold.
  const getDevicesWithHighParentLatency = (thresholdMs) => {
    if (thresholdMs === null || thresholdMs === undefined) return [];
    return devices.filter(device => {
      if (!device.connected) return false;
      const ms = getDeviceParentLatencyMs(device);
      if (ms === null) return false;
      return ms > thresholdMs;
    });
  };

  // Filter devices with packet loss > threshold
  // ONLY includes devices where backhaul_tx_failed_percentage field exists and has a valid numeric value > 0
  // A device is EXCLUDED if value is: missing, null, "N/A", 0, 0.0, or non-numeric
  // Threshold filtering is applied ONLY AFTER validity filtering
  const getDevicesWithHighPacketLoss = (threshold) => {
    if (threshold === null || threshold === undefined) return [];
    
    return devices.filter(device => {
      if (!device.connected) return false;
      const packetLoss = device.backhaul_tx_failed_percentage;
      // Step 1: Filter out invalid devices (missing, null, "N/A", zero, non-numeric)
      if (packetLoss === undefined || packetLoss === null) return false;
      if (typeof packetLoss === 'string' && (packetLoss.toLowerCase() === 'n/a' || packetLoss.trim() === '')) return false;
      const numLoss = typeof packetLoss === 'string' ? parseFloat(packetLoss) : packetLoss;
      if (isNaN(numLoss) || !isFinite(numLoss)) return false;
      if (numLoss === 0 || numLoss === 0.0) return false; // Exclude zero values
      // Step 2: Apply threshold comparison (only to valid devices)
      return numLoss > threshold;
    });
  };

  // Get device connected_time value from WiFi associations (parent device's associations)
  // Returns null if connected_time cannot be found or is invalid
  // A device is EXCLUDED if value is: missing, null, "N/A", 0, 0.0, negative, or non-numeric
  // DO NOT use uptime - connected_time is the ONLY valid metric for network connection duration
  // Uses the SAME matching logic as DeviceDetails page for consistency
  const getDeviceConnectedTime = (device) => {
    // Connected time comes from parent device's WiFi associations, not a direct field
    if (!device || !device.parent_serial) {
      if (device && Math.random() < 0.05) { // 5% sample for debugging
        console.log('[DEBUG ConnectedTime] Device', device.serial_number, 'has no parent_serial');
      }
      return null;
    }
    
    // Find parent device
    const parentDevice = devices.find(d => 
      d.serial_number && 
      d.serial_number.toLowerCase() === device.parent_serial.toLowerCase()
    );
    
    if (!parentDevice) {
      if (Math.random() < 0.05) {
        console.log('[DEBUG ConnectedTime] Parent device not found for', device.serial_number, 'parent_serial:', device.parent_serial);
      }
      return null;
    }
    
    if (!parentDevice.private_wifi?.interfaces && !parentDevice.private_wifi?.Interfaces) {
      if (Math.random() < 0.05) {
        console.log('[DEBUG ConnectedTime] Parent device', parentDevice.serial_number, 'has no WiFi interfaces');
      }
      return null;
    }
    
    // Build device identifiers: MACs from radios AND serial number
    // jnet2 uses serial number in associations, EdgeCore uses MAC
    // This matches the logic used in DeviceDetails.js
    const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
    const deviceSerial = device.serial_number?.toLowerCase();
    const deviceIdentifiers = new Set(deviceMacs);
    if (deviceSerial) {
      deviceIdentifiers.add(deviceSerial);
      deviceIdentifiers.add(deviceSerial.replace(/:/g, ''));
    }
    if (deviceIdentifiers.size === 0) {
      if (Math.random() < 0.05) {
        console.log('[DEBUG ConnectedTime] Device', device.serial_number, 'has no identifiers (no radios MACs or serial)');
      }
      return null;
    }
    
    // Search parent's WiFi associations for this device and get ConnectedTime
    const parentIfaces = parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces;
    for (const iface of parentIfaces || []) {
      // Handle both Associations/associations for compatibility
      const associations = iface.Associations || iface.associations;
      if (associations) {
        for (const assoc of associations) {
          // Handle both Station/station and ConnectedTime/connected_time
          const station = assoc.Station || assoc.station;
          const connectedTime = assoc.ConnectedTime || assoc.connected_time;
          if (!station || !connectedTime) continue;
          
          // Match using the same logic as DeviceDetails: check normalized station ID and lowercase station
          const stationId = station?.toLowerCase().replace(/:/g, '');
          if ((deviceIdentifiers.has(stationId) || deviceIdentifiers.has(station?.toLowerCase())) && connectedTime) {
            // Strict validation - exclude invalid values
            // Step 1: Check if field exists and is not null/undefined
            if (connectedTime === undefined || connectedTime === null) {
              if (Math.random() < 0.1) {
                console.log('[DEBUG ConnectedTime] Device', device.serial_number, 'has null/undefined connectedTime');
              }
              return null;
            }
            // Step 2: Check if it's a valid string (not "N/A")
            if (typeof connectedTime === 'string' && (connectedTime.toLowerCase() === 'n/a' || connectedTime.trim() === '')) {
              if (Math.random() < 0.1) {
                console.log('[DEBUG ConnectedTime] Device', device.serial_number, 'has "N/A" connectedTime');
              }
              return null;
            }
            // Step 3: Convert to number if string
            const numValue = typeof connectedTime === 'string' ? parseFloat(connectedTime) : connectedTime;
            // Step 4: Check if it's a valid number
            if (isNaN(numValue) || !isFinite(numValue)) {
              if (Math.random() < 0.1) {
                console.log('[DEBUG ConnectedTime] Device', device.serial_number, 'has non-numeric connectedTime:', connectedTime);
              }
              return null;
            }
            // Step 5: Check if it's greater than 0
            if (numValue <= 0) {
              if (Math.random() < 0.1) {
                console.log('[DEBUG ConnectedTime] Device', device.serial_number, 'has non-positive connectedTime:', numValue);
              }
              return null;
            }
            // Valid connected time found
            return numValue;
          }
        }
      }
    }
    
    // Connected time not found in associations
    if (Math.random() < 0.05) {
      console.log('[DEBUG ConnectedTime] Device', device.serial_number, 'identifiers', Array.from(deviceIdentifiers), 'not found in parent associations');
    }
    return null;
  };

  // Filter devices with connected time < threshold (in seconds)
  // ONLY includes devices where connected_time can be found from WiFi associations and has a valid numeric value > 0
  // A device is EXCLUDED if:
  // - Device has no parent_serial (cannot get connected_time from associations)
  // - Parent device not found or has no WiFi associations
  // - Device's station MAC not found in parent's associations
  // - connected_time is null, "N/A", 0, 0.0, negative, or non-numeric
  // DO NOT use uptime - connected_time from associations is the ONLY valid metric
  // Threshold filtering is applied ONLY AFTER validity filtering
  // CRITICAL: threshold is in SECONDS, connected_time is in SECONDS - comparison is direct
  const getDevicesWithLowConnectedTime = (threshold) => {
    if (threshold === null || threshold === undefined || threshold <= 0) {
      console.log('[DEBUG ConnectedTime] Threshold is invalid:', threshold);
      return [];
    }
    
    const thresholdHours = threshold / 3600;
    console.log('[DEBUG ConnectedTime] Filtering with threshold:', threshold, 'seconds (', thresholdHours, 'hours)');
    
    const filtered = devices.filter(device => {
      // Step 1: Only connected devices
      if (!device.connected) return false;
      
      // Step 2: Get connected_time from associations (returns null if invalid)
      const connectedTimeSec = getDeviceConnectedTime(device);
      
      // Step 3: Filter out invalid devices (missing, null, "N/A", zero, non-numeric)
      if (connectedTimeSec === null) return false;
      
      // Step 4: Convert to hours for logging
      const connectedTimeHours = connectedTimeSec / 3600;
      
      // Step 5: Apply threshold comparison (connected_time_sec < threshold_sec)
      const isBelowThreshold = connectedTimeSec < threshold;
      
      // Debug logging for devices being evaluated
      if (isBelowThreshold || Math.random() < 0.1) { // Log all below threshold + 10% sample
        console.log('[DEBUG ConnectedTime] Device:', device.serial_number, 
          '| Raw connected_time (sec):', connectedTimeSec,
          '| Converted (hours):', connectedTimeHours.toFixed(2),
          '| Threshold (hours):', thresholdHours.toFixed(2),
          '| Below threshold:', isBelowThreshold);
      }
      
      return isBelowThreshold;
    });
    
    console.log('[DEBUG ConnectedTime] Total devices below threshold:', filtered.length, 'out of', devices.length, 'connected devices');
    return filtered;
  };

  const getDeviceIcon = (deviceType) => {
    const type = deviceType.toLowerCase();
    if (type.includes('gateway') || type.includes('cap')) return <RouterIcon />;
    if (type.includes('mesh') || type.includes('node')) return <WifiTetheringIcon />;
    if (type.includes('camera') || type.includes('ai') || type.includes('nvidia')) return <VideocamIcon />;
    if (type.includes('sensor') || type.includes('hub')) return <SensorsIcon />;
    return <HubIcon />;
  };

  // Removed getSystemStatus - no global aggregations allowed

  const getHealthIndicator = (connected, offline) => {
    // UI-only status coloring based on requirements:
    // - offline == 0 → GREEN
    // - offline > 0 AND offline < connected → YELLOW
    // - offline >= connected → RED
    if (offline === 0) {
      return { color: JUGANU_GREEN, label: 'Healthy', status: 'healthy' };
    }
    if (offline > 0 && offline < connected) {
      return { color: JUGANU_YELLOW, label: 'Warning', status: 'warning' };
    }
    return { color: JUGANU_RED, label: 'Critical', status: 'critical' };
  };

  const getConnectivityRatio = (connected, offline) => {
    const total = connected + offline;
    if (total === 0) return null;
    return (connected / total) * 100;
  };

  // Color mapping for backhaul latency in milliseconds (lower is better).
  //   <= 30 ms  → Green
  //   <= 50 ms  → Yellow
  //   >  50 ms  → Red
  const getLatencyMsColor = (ms) => {
    if (ms === null || ms === undefined) return JUGANU_GREEN;
    if (ms <= 30) return JUGANU_GREEN;
    if (ms <= 50) return JUGANU_YELLOW;
    return JUGANU_RED;
  };

  // Packet Loss color mapping (percentage, lower is better)
  // <= 0.1% → Green
  // 0.1% - 1% → Yellow
  // > 1% → Red
  const getPacketLossColor = (loss) => {
    if (loss <= 0.1) return JUGANU_GREEN;
    if (loss <= 1) return JUGANU_YELLOW;
    return JUGANU_RED;
  };

  // Format packet loss with smart rounding for very small values
  // Handles extremely small values (e.g., 7e-7) and never shows "0.00%" unless exactly zero
  const formatPacketLoss = (loss) => {
    if (loss === null || loss === undefined) return 'N/A';
    
    // Handle exactly zero
    if (loss === 0) return '0.00%';
    
    // Convert to number if it's a string (handles scientific notation)
    const numLoss = typeof loss === 'string' ? parseFloat(loss) : loss;
    
    // Handle NaN or invalid values
    if (isNaN(numLoss) || !isFinite(numLoss)) return 'N/A';
    
    // Handle extremely small values (< 0.001%)
    if (numLoss < 0.001) return '< 0.001%';
    
    // For values >= 0.001%, show appropriate precision
    if (numLoss < 0.01) return numLoss.toFixed(3) + '%';
    if (numLoss < 1) return numLoss.toFixed(2) + '%';
    return numLoss.toFixed(2) + '%';
  };

  // Memoize these so they're stable deps and don't create new {} on every render.
  // Hooks must be called unconditionally (even when tab is inactive/loading/error).
  const devicesByType = useMemo(() => networkData?.devices_by_type || {}, [networkData]);
  const devicesByTypeDetailed = useMemo(() => networkData?.devices_by_type_detailed || {}, [networkData]);
  const sourceData = useMemo(
    () => (Object.keys(devicesByTypeDetailed).length > 0 ? devicesByTypeDetailed : devicesByType),
    [devicesByType, devicesByTypeDetailed]
  );
  const softwareVersionsByType = useMemo(
    () => networkData?.software_versions_by_type || networkData?.software_versions || {},
    [networkData]
  );
  const provisioningMismatchCount = provisioningMismatch?.count ?? 0;
  const provisioningMismatches = provisioningMismatch?.mismatches ?? [];

  // Mesh nodes by live STA uplink band (ul_band from state report).
  // CAP / AI / EdgeCore devices are excluded — only Juganu Nodes
  // carry a treemgmt STA radio. Must stay above early returns.
  const treeStaBandDistribution = useMemo(() => {
    const counts = { '5GHz': 0, '6GHz': 0, other: 0, offline: 0 };
    const nodes = devices.filter((d) => isMeshBackhaulNode(d.capabilities));
    for (const device of nodes) {
      const bucket = getMeshStaBandFromDevice(device);
      counts[bucket] = (counts[bucket] || 0) + 1;
    }
    const total = nodes.length;
    const pct = (n) => (total > 0 ? (n / total) * 100 : 0);
    return {
      total,
      band5: counts['5GHz'],
      band6: counts['6GHz'],
      other: counts.other,
      offline: counts.offline,
      pct5: pct(counts['5GHz']),
      pct6: pct(counts['6GHz']),
      pctOther: pct(counts.other),
      pctOffline: pct(counts.offline),
    };
  }, [devices]);

  const treeStaBandRows = useMemo(() => ([
    {
      key: '5GHz',
      label: '5 GHz',
      count: treeStaBandDistribution.band5,
      pct: treeStaBandDistribution.pct5,
    },
    {
      key: '6GHz',
      label: '6 GHz',
      count: treeStaBandDistribution.band6,
      pct: treeStaBandDistribution.pct6,
    },
    ...(treeStaBandDistribution.other > 0 ? [{
      key: 'other',
      label: 'Other',
      count: treeStaBandDistribution.other,
      pct: treeStaBandDistribution.pctOther,
    }] : []),
    ...(treeStaBandDistribution.offline > 0 ? [{
      key: 'offline',
      label: 'Offline',
      count: treeStaBandDistribution.offline,
      pct: treeStaBandDistribution.pctOffline,
      isOffline: true,
    }] : []),
  ]), [treeStaBandDistribution]);

  if (loading && !networkData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !networkData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleRefresh} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  // Topology metrics - STRICT: use exact field names from API
  const topologyMetrics = networkData?.topology_metrics || {};
  const maxConnectedNodesPerCap = topologyMetrics.max_connected_nodes_per_cap ?? null;
  const minConnectedNodesPerCap = topologyMetrics.min_connected_nodes_per_cap ?? null;
  const maxTreeDepthConnected = topologyMetrics.max_tree_depth_connected ?? null;
  
  const networkQuality = networkData?.network_quality_metrics || networkData?.network_quality || {};
  // Backhaul latency: average across connected devices of each device's
  // median round-trip latency to its parent (milliseconds, lower is better).
  const avgParentLatencyMs = networkQuality.avg_parent_latency_ms ?? null;
  const packetLoss = networkQuality.avg_packet_loss_pct ?? (networkQuality.avg_packet_loss ?? (networkQuality.packet_loss_percent ?? null));
  
  // Connected time metrics - convert seconds to hours
  const minConnectedTimeSec = networkQuality.min_connected_time_sec ?? null;
  const avgConnectedTimeSec = networkQuality.avg_connected_time_sec ?? null;
  const medianConnectedTimeSec = networkQuality.median_connected_time_sec ?? null;
  
  const convertSecondsToHours = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    return (seconds / 3600).toFixed(1);
  };
  
  const minConnectedTimeHours = convertSecondsToHours(minConnectedTimeSec);
  const avgConnectedTimeHours = convertSecondsToHours(avgConnectedTimeSec);
  const medianConnectedTimeHours = convertSecondsToHours(medianConnectedTimeSec);

  return (
    <Box sx={{ 
      bgcolor: 'background.default',
      minHeight: '100%',
      p: { xs: 1, sm: 1.5, md: 2 }
    }}>
      {/* Header with Auto-Refresh Toggle */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 1, md: 1.5 },
        pt: 0,
        pb: 0.5,
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 500, 
            fontSize: { xs: '0.9rem', sm: '1rem' },
            lineHeight: 1.2,
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
            color: 'text.primary'
          }}
        >
          Overview — Network & Installation Status
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, sm: 1.5 }, 
          flexShrink: 0,
          flexWrap: 'wrap'
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={handleAutoRefreshToggle}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' }, whiteSpace: 'nowrap' }}>
                {isMobile ? 'Auto' : 'Auto Refresh (2 min)'}
              </Typography>
            }
            sx={{ margin: 0 }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={handleRefresh}
            disabled={loading}
            startIcon={<RefreshIcon />}
            sx={{ 
              minWidth: 'auto',
              px: { xs: 1, sm: 1.5 },
              py: 0.5,
              fontSize: { xs: '0.75rem', sm: '0.8125rem' }
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Section 1: Devices by Type - Full Width */}
      <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
        <Typography variant="caption" sx={{ 
          display: 'block',
          mb: { xs: 0.75, md: 1 },
          fontSize: { xs: '0.65rem', md: '0.7rem' },
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          color: 'text.secondary'
        }}>
          Devices by Type
        </Typography>
        
        {/* Mobile: Card View */}
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Object.entries(sourceData)
              .map(([type, data]) => {
                let connected = 0;
                let offline = 0;
                
                if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
                  connected = typeof data.connected === 'number' ? data.connected : 0;
                  offline = typeof data.offline === 'number' ? data.offline : 0;
                } else if (typeof data === 'number') {
                  connected = data;
                  offline = 0;
                }
                
                const total = connected + offline;
                if (total === 0) return null;
                
                const ratio = getConnectivityRatio(connected, offline);
                const health = getHealthIndicator(connected, offline);
                const connectedPercent = ratio !== null ? ratio : 0;
                const offlinePercent = ratio !== null ? (100 - ratio) : 0;
                
                return { type, connected, offline, total, health, connectedPercent, offlinePercent };
              })
              .filter(item => item !== null)
              .sort((a, b) => {
                const statusRank = (status) => {
                  if (status === 'critical') return 0;
                  if (status === 'warning') return 1;
                  if (status === 'healthy') return 2;
                  return 3;
                };
                const byStatus = statusRank(a.health.status) - statusRank(b.health.status);
                if (byStatus !== 0) return byStatus;
                if (b.offline !== a.offline) return b.offline - a.offline;
                return a.type.localeCompare(b.type);
              })
              .map(({ type, connected, offline, total, health, connectedPercent, offlinePercent }) => {
                const isExpanded = expandedDeviceTypes[type];
                const canExpand = offline > 0;
                const offlineDevices = canExpand ? getOfflineDevicesByType(type) : [];
                
                return (
                  <Card 
                    key={type}
                    sx={{ 
                      border: 1, 
                      borderColor: 'divider',
                      borderRadius: 1,
                      '&:hover': canExpand ? { boxShadow: 2 } : {}
                    }}
                  >
                    <CardContent 
                      sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}
                      onClick={canExpand ? () => toggleDeviceTypeExpand(type) : undefined}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {canExpand && (
                            <ChevronRightIcon 
                              sx={{ 
                                fontSize: 18,
                                color: 'text.secondary',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }} 
                            />
                          )}
                          <Box sx={{ 
                            p: 0.5,
                            borderRadius: 0.5,
                            bgcolor: 'action.hover',
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {getDeviceIcon(type)}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            {type}
                          </Typography>
                        </Box>
                        <Chip
                          label={health.label}
                          size="small"
                          sx={{
                            ...overviewStatusChipSx,
                            fontSize: '0.65rem',
                            height: 22,
                            '& .MuiChip-label': { px: 1 },
                          }}
                          icon={<Box sx={overviewStatusChipIconSx} />}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                        <span>{connected} Connected</span>
                        {offline > 0 && (
                          <span style={{ color: JUGANU_RED, fontWeight: 700 }}>
                            {offline} Offline
                          </span>
                        )}
                        <span style={{ fontWeight: 600 }}>Total: {total}</span>
                      </Box>
                      
                      <Box sx={{ 
                        width: '100%',
                        height: 8,
                        bgcolor: 'action.hover',
                        borderRadius: '9999px',
                        overflow: 'hidden',
                        display: 'flex'
                      }}>
                        {connectedPercent > 0 && (
                          <Box sx={{ 
                            height: '100%',
                            bgcolor: health.status === 'healthy' ? JUGANU_GREEN : health.status === 'warning' ? JUGANU_YELLOW : JUGANU_RED,
                            width: `${connectedPercent}%`,
                            transition: 'width 0.3s ease'
                          }} />
                        )}
                        {offlinePercent > 0 && (
                          <Box sx={{ 
                            height: '100%',
                            bgcolor: 'action.disabled',
                            width: `${offlinePercent}%`
                          }} />
                        )}
                      </Box>
                      
                      {canExpand && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="caption" sx={{ 
                              display: 'block',
                              mb: 1,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              color: 'text.secondary'
                            }}>
                              Offline Devices ({offlineDevices.length})
                            </Typography>
                            {offlineDevices.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {offlineDevices.map((device) => (
                                  <Box 
                                    key={device.serial_number}
                                    sx={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      p: 0.75,
                                      bgcolor: 'action.hover',
                                      borderRadius: 0.5
                                    }}
                                  >
                                    <Link
                                      component="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/device/${device.serial_number?.toLowerCase() || device.serial_number}`);
                                      }}
                                      sx={{
                                        fontSize: '0.8rem',
                                        fontFamily: 'monospace',
                                        textDecoration: 'none',
                                        color: '#1976d2',
                                        cursor: 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        textAlign: 'left',
                                        '&:hover': {
                                          textDecoration: 'underline',
                                          color: '#1565c0'
                                        }
                                      }}
                                    >
                                      {device.serial_number}
                                    </Link>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                      {formatRelativeTime(device.last_seen)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
                                No offline devices found
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            {Object.keys(sourceData).length === 0 && (
              <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                No device type data available
              </Typography>
            )}
          </Box>
        ) : (
          /* Desktop: Table View */
          <Paper sx={{ 
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden'
          }}>
            <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ 
                    py: 1,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    color: 'text.secondary'
                  }}>
                    Device Type
                  </TableCell>
                  <TableCell sx={{ 
                    py: 1,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    color: 'text.secondary',
                    width: '33%'
                  }}>
                    Connectivity
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    py: 1,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    color: 'text.secondary'
                  }}>
                    Count
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    py: 1,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    color: 'text.secondary'
                  }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
                <TableBody>
                  {Object.entries(sourceData)
                    .map(([type, data]) => {
                      let connected = 0;
                      let offline = 0;
                      
                      if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
                        connected = typeof data.connected === 'number' ? data.connected : 0;
                        offline = typeof data.offline === 'number' ? data.offline : 0;
                      } else if (typeof data === 'number') {
                        connected = data;
                        offline = 0;
                      }
                      
                      const total = connected + offline;
                      if (total === 0) return null;
                      
                      const ratio = getConnectivityRatio(connected, offline);
                      const health = getHealthIndicator(connected, offline);
                      const connectedPercent = ratio !== null ? ratio : 0;
                      const offlinePercent = ratio !== null ? (100 - ratio) : 0;
                      
                      return { type, connected, offline, total, health, connectedPercent, offlinePercent };
                    })
                    .filter(item => item !== null)
                    .sort((a, b) => {
                      // Sort by severity: Critical (red) → Warning (yellow) → Healthy (green)
                      const statusRank = (status) => {
                        if (status === 'critical') return 0;
                        if (status === 'warning') return 1;
                        if (status === 'healthy') return 2;
                        return 3;
                      };
                      const byStatus = statusRank(a.health.status) - statusRank(b.health.status);
                      if (byStatus !== 0) return byStatus;
                      // Then by offline count (descending)
                      if (b.offline !== a.offline) return b.offline - a.offline;
                      // Finally alphabetically
                      return a.type.localeCompare(b.type);
                    })
                    .map(({ type, connected, offline, total, health, connectedPercent, offlinePercent }) => {
                      const isExpanded = expandedDeviceTypes[type];
                      const canExpand = offline > 0;
                      const offlineDevices = canExpand ? getOfflineDevicesByType(type) : [];
                      
                      return (
                        <React.Fragment key={type}>
                          <TableRow 
                            sx={{
                              '&:hover': canExpand ? { bgcolor: 'action.hover', cursor: 'pointer' } : { bgcolor: 'action.hover' },
                              transition: 'background-color 0.2s'
                            }}
                            onClick={canExpand ? () => toggleDeviceTypeExpand(type) : undefined}
                          >
                            <TableCell sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {canExpand && (
                                  <ChevronRightIcon 
                                    sx={{ 
                                      fontSize: 16,
                                      color: 'text.secondary',
                                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.2s'
                                    }} 
                                  />
                                )}
                                {!canExpand && offline === 0 && (
                                  <Box sx={{ width: 16 }} />
                                )}
                                <Box sx={{ 
                                  p: 0.5,
                                  borderRadius: 0.5,
                                  bgcolor: 'action.hover',
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {getDeviceIcon(type)}
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                  {type}
                                </Typography>
                                {!canExpand && offline === 0 && (
                                  <Tooltip title="No offline devices">
                                    <Box sx={{ ml: 'auto' }} />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                              <span>{connected} Connected</span>
                              {offline > 0 && (
                                <span style={{ color: JUGANU_RED, fontWeight: 700 }}>
                                  {offline} Offline
                                </span>
                              )}
                            </Box>
                            <Box sx={{ 
                              width: '100%',
                              height: 6,
                              bgcolor: 'action.hover',
                              borderRadius: '9999px',
                              overflow: 'hidden',
                              display: 'flex'
                            }}>
                              {connectedPercent > 0 && (
                                <Box sx={{ 
                                  height: '100%',
                                  bgcolor: health.status === 'healthy' ? JUGANU_GREEN : health.status === 'warning' ? JUGANU_YELLOW : JUGANU_RED,
                                  width: `${connectedPercent}%`,
                                  transition: 'width 0.3s ease'
                                }} />
                              )}
                              {offlinePercent > 0 && (
                                <Box sx={{ 
                                  height: '100%',
                                  bgcolor: 'action.disabled',
                                  width: `${offlinePercent}%`
                                }} />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '0.875rem' }}>
                            {total}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Chip
                            label={health.label}
                            size="small"
                            sx={{
                              ...overviewStatusChipSx,
                              fontSize: '0.65rem',
                              height: 20,
                              '& .MuiChip-label': { px: 1 },
                            }}
                            icon={<Box sx={overviewStatusChipIconSx} />}
                          />
                        </TableCell>
                      </TableRow>
                      {canExpand && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ 
                                bgcolor: 'background.default',
                                borderTop: 1,
                                borderColor: 'divider',
                                px: 2,
                                py: 1.5
                              }}>
                                <Typography variant="caption" sx={{ 
                                  display: 'block',
                                  mb: 1,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 1,
                                  color: 'text.secondary'
                                }}>
                                  Offline Devices ({offlineDevices.length})
                                </Typography>
                                {offlineDevices.length > 0 ? (
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                          <TableCell sx={{ 
                                            py: 0.5,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            color: 'text.secondary'
                                          }}>
                                            Serial Number
                                          </TableCell>
                                          <TableCell align="right" sx={{ 
                                            py: 0.5,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            color: 'text.secondary'
                                          }}>
                                            Last Seen
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {offlineDevices.map((device) => (
                                          <TableRow 
                                            key={device.serial_number}
                                            sx={{
                                              '&:hover': { bgcolor: 'action.hover' }
                                            }}
                                          >
                                            <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace', textAlign: 'left' }}>
                                              <Link
                                                component="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate(`/device/${device.serial_number?.toLowerCase() || device.serial_number}`);
                                                }}
                                                sx={{
                                                  fontSize: '0.8rem',
                                                  fontFamily: 'monospace',
                                                  textDecoration: 'none',
                                                  color: '#1976d2',
                                                  cursor: 'pointer',
                                                  background: 'none',
                                                  border: 'none',
                                                  padding: 0,
                                                  textAlign: 'left',
                                                  display: 'block',
                                                  '&:hover': {
                                                    textDecoration: 'underline',
                                                    color: '#1565c0'
                                                  }
                                                }}
                                              >
                                                {device.serial_number}
                                              </Link>
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', color: 'text.secondary' }}>
                                              {formatRelativeTime(device.last_seen)}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
                                    No offline devices found
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                    );
                    })}
                {Object.keys(sourceData).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                      No device type data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Provisioning Mismatches */}
      <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
        <Typography variant="caption" sx={{
          display: 'block',
          mb: { xs: 0.75, md: 1 },
          fontSize: { xs: '0.65rem', md: '0.7rem' },
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          color: 'text.secondary'
        }}>
          Provisioning Mismatches
        </Typography>
        <Paper sx={{
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 1, md: 1.5 },
              py: { xs: 1, md: 1.25 },
              cursor: provisioningMismatchCount > 0 ? 'pointer' : 'default',
              bgcolor: provisioningMismatchCount > 0 ? 'warning.light' : 'action.hover',
            }}
            onClick={() => {
              if (provisioningMismatchCount > 0) {
                setExpandedProvisioningMismatch((prev) => !prev);
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {provisioningMismatchCount > 0 && (
                <ChevronRightIcon
                  sx={{
                    fontSize: 20,
                    transform: expandedProvisioningMismatch ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                />
              )}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Provisioning Mismatches
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {provisioningMismatchCount}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={provisioningMismatchCount > 0 ? `${provisioningMismatchCount} mismatch${provisioningMismatchCount === 1 ? '' : 'es'}` : '0 mismatches'}
              color={provisioningMismatchCount > 0 ? 'warning' : 'success'}
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <Collapse in={expandedProvisioningMismatch && provisioningMismatchCount > 0}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>MAC</TableCell>
                    <TableCell>Serial</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Parent Serial</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {provisioningMismatches.map((entry) => (
                    <TableRow key={`${entry.mac_address}-${entry.reason}-${entry.parent_serial || ''}`}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {entry.mac_address || '—'}
                      </TableCell>
                      <TableCell>
                        {entry.serial_number ? (
                          <Link
                            component="button"
                            variant="body2"
                            sx={{ fontFamily: 'monospace', textAlign: 'left' }}
                            onClick={() => navigate(`/device/${entry.serial_number}`)}
                          >
                            {entry.serial_number}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {PROVISIONING_MISMATCH_REASON_LABELS[entry.reason] || entry.reason || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {entry.parent_serial || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>
      </Box>

      {/* Section 2: Topology Metrics + Tree Band (STA) + Network Quality */}
      <Grid container spacing={{ xs: 1, md: 2 }} sx={{ mb: { xs: 1.5, md: 2 } }}>
        <Grid item xs={12} md={sectionTwoGridMd}>
          <Typography variant="caption" sx={{ 
            display: 'block',
            mb: { xs: 0.75, md: 1 },
            fontSize: { xs: '0.65rem', md: '0.7rem' },
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            color: 'text.secondary'
          }}>
            Topology Metrics
          </Typography>
          <Paper sx={{ 
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            p: { xs: 1, md: 1.5 },
            height: '100%'
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, md: 1 } }}>
              <Box sx={{ 
                p: { xs: 0.75, md: 1 },
                bgcolor: 'action.hover',
                borderRadius: 0.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="caption" sx={{ 
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'text.secondary'
                }}>
                  Max Nodes per CAP
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: { xs: '1.25rem', md: '1.75rem' } }}>
                  {maxConnectedNodesPerCap === null || maxConnectedNodesPerCap === undefined ? '—' : maxConnectedNodesPerCap}
                </Typography>
              </Box>
              <Box sx={{ 
                p: { xs: 0.75, md: 1 },
                bgcolor: 'action.hover',
                borderRadius: 0.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="caption" sx={{ 
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'text.secondary'
                }}>
                  Min Nodes per CAP
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: { xs: '1.25rem', md: '1.75rem' } }}>
                  {minConnectedNodesPerCap === null || minConnectedNodesPerCap === undefined ? '—' : minConnectedNodesPerCap}
                </Typography>
              </Box>
              <Box sx={{ 
                p: { xs: 0.75, md: 1 },
                bgcolor: 'action.hover',
                borderRadius: 0.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="caption" sx={{ 
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'text.secondary'
                }}>
                  Max Tree Depth
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: { xs: '1.25rem', md: '1.75rem' } }}>
                  {maxTreeDepthConnected === null || maxTreeDepthConnected === undefined ? '—' : maxTreeDepthConnected}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {showTreeStaBand && (
        <Grid item xs={12} md={sectionTwoGridMd}>
          <Typography variant="caption" sx={{
            display: 'block',
            mb: { xs: 0.75, md: 1 },
            fontSize: { xs: '0.65rem', md: '0.7rem' },
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            color: 'text.secondary',
          }}
          >
            Tree Band (STA)
          </Typography>
          <Paper sx={{
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            p: { xs: 1, md: 1.5 },
            height: '100%',
          }}
          >
            <Typography variant="caption" sx={{
              display: 'block',
              mb: 1,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              color: 'text.secondary',
            }}
            >
              Juganu Nodes by live uplink band ({treeStaBandDistribution.total} total)
            </Typography>
            {treeStaBandDistribution.total === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
                No mesh nodes in the current device list
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, md: 1 } }}>
                {treeStaBandRows.map((row) => (
                  <Box
                    key={row.key}
                    sx={{
                      p: { xs: 0.75, md: 1 },
                      bgcolor: 'action.hover',
                      borderRadius: 0.5,
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                    >
                      <Typography variant="caption" sx={{
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                      }}
                      >
                        {row.label}
                      </Typography>
                      <Typography variant="h4" sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', md: '1.75rem' },
                        color: row.isOffline ? 'text.disabled' : 'text.primary',
                      }}
                      >
                        {row.count}
                      </Typography>
                    </Box>
                    <Box sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: 'action.selected',
                      borderRadius: '9999px',
                      overflow: 'hidden',
                    }}
                    >
                      <Box sx={{
                        height: '100%',
                        width: `${row.pct}%`,
                        bgcolor: row.isOffline ? 'action.disabled' : MAP_UI.accentMuted,
                        transition: 'width 0.3s ease',
                        minWidth: row.count > 0 ? 4 : 0,
                      }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{
                      display: 'block',
                      mt: 0.5,
                      fontSize: '0.7rem',
                      color: 'text.secondary',
                      fontFamily: 'monospace',
                    }}
                    >
                      {row.pct.toFixed(0)}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        )}

        <Grid item xs={12} md={sectionTwoGridMd}>
          <Typography variant="caption" sx={{ 
            display: 'block',
            mb: { xs: 0.75, md: 1 },
            fontSize: { xs: '0.65rem', md: '0.7rem' },
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            color: 'text.secondary'
          }}>
            Network Quality
          </Typography>
          <Paper sx={{ 
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            p: { xs: 1, md: 1.5 },
            height: '100%'
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, md: 1 } }}>
              {avgParentLatencyMs !== null && avgParentLatencyMs !== undefined && (() => {
                const metricKey = 'parentLatency';
                const isExpanded = expandedNetworkQuality[metricKey];
                // Default the affected-devices threshold to the fleet average
                // so the expanded view is meaningful out of the box.
                const threshold = networkQualityThresholds.latency !== null
                  ? networkQualityThresholds.latency
                  : avgParentLatencyMs;
                const affectedDevices = getDevicesWithHighParentLatency(threshold);
                const headlineLatency = formatLatencyMs(avgParentLatencyMs);

                return (
                  <Box key={metricKey}>
                    <Box sx={{
                      p: { xs: 0.75, md: 1 },
                      bgcolor: 'action.hover',
                      borderRadius: 0.5
                    }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 0.5,
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleNetworkQualityExpand(metricKey)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ChevronRightIcon
                            sx={{
                              fontSize: { xs: 14, md: 16 },
                              color: 'text.secondary',
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s'
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                            Avg Latency to Parent
                          </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 700, color: getLatencyMsColor(avgParentLatencyMs), fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                          {headlineLatency}
                        </Typography>
                      </Box>
                      <Box sx={{
                        height: 4,
                        width: '100%',
                        bgcolor: 'action.selected',
                        borderRadius: '9999px',
                        overflow: 'hidden',
                        mb: isExpanded ? 1 : 0
                      }}>
                        <Box sx={{
                          height: '100%',
                          bgcolor: getLatencyMsColor(avgParentLatencyMs),
                          // Anchor the bar at 200ms == "full red"; clamp 0-100%.
                          width: `${Math.min((avgParentLatencyMs / 200) * 100, 100)}%`
                        }} />
                      </Box>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              label="Threshold (ms)"
                              value={networkQualityThresholds.latency !== null ? networkQualityThresholds.latency : Number(avgParentLatencyMs.toFixed(1))}
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : parseFloat(e.target.value);
                                setNetworkQualityThresholds(prev => ({ ...prev, latency: value }));
                              }}
                              sx={{ width: 120 }}
                              inputProps={{ min: 0, step: 1 }}
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                              Showing devices with latency to parent &gt; {Number(threshold).toFixed(1)} ms ({affectedDevices.length} devices)
                            </Typography>
                          </Box>
                          {affectedDevices.length > 0 ? (
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Device Type</TableCell>
                                    <TableCell sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Serial Number</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Latency to Parent</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {affectedDevices.map((device) => {
                                    const deviceLatencyMs = getDeviceParentLatencyMs(device);
                                    return (
                                      <TableRow key={device.serial_number} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>{getDeviceType(device.capabilities)}</TableCell>
                                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace', textAlign: 'left' }}>
                                          <Link
                                            component="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate(`/device/${device.serial_number?.toLowerCase() || device.serial_number}`);
                                            }}
                                            sx={{
                                              fontSize: '0.8rem',
                                              fontFamily: 'monospace',
                                              textDecoration: 'none',
                                              color: '#1976d2',
                                              cursor: 'pointer',
                                              background: 'none',
                                              border: 'none',
                                              padding: 0,
                                              textAlign: 'left',
                                              display: 'block',
                                              '&:hover': {
                                                textDecoration: 'underline',
                                                color: '#1565c0'
                                              }
                                            }}
                                          >
                                            {device.serial_number}
                                          </Link>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace', color: deviceLatencyMs !== null ? getLatencyMsColor(deviceLatencyMs) : 'text.secondary' }}>
                                          {deviceLatencyMs !== null ? formatLatencyMs(deviceLatencyMs) : 'N/A'}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
                              No devices found with latency to parent &gt; {Number(threshold).toFixed(1)} ms
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                );
              })()}
              {packetLoss !== null && packetLoss !== undefined && (() => {
                const metricKey = 'packetLoss';
                const isExpanded = expandedNetworkQuality[metricKey];
                const threshold = networkQualityThresholds.packetLoss !== null ? networkQualityThresholds.packetLoss : packetLoss;
                const affectedDevices = getDevicesWithHighPacketLoss(threshold);
                
                return (
                  <Box key={metricKey}>
                    <Box sx={{ 
                      p: { xs: 0.75, md: 1 },
                      bgcolor: 'action.hover',
                      borderRadius: 0.5
                    }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          mb: 0.5,
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleNetworkQualityExpand(metricKey)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ChevronRightIcon 
                            sx={{ 
                              fontSize: { xs: 14, md: 16 },
                              color: 'text.secondary',
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s'
                            }} 
                          />
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                            Packet Loss (Backhaul)
                          </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 700, color: getPacketLossColor(packetLoss), fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                          {formatPacketLoss(packetLoss)}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        height: 4,
                        width: '100%',
                        bgcolor: 'action.selected',
                        borderRadius: '9999px',
                        overflow: 'hidden',
                        mb: isExpanded ? 1 : 0
                      }}>
                        <Box sx={{ 
                          height: '100%',
                          bgcolor: getPacketLossColor(packetLoss),
                          width: `${Math.min(packetLoss * 10, 100)}%`
                        }} />
                      </Box>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              label="Threshold (%)"
                              value={networkQualityThresholds.packetLoss !== null ? networkQualityThresholds.packetLoss : packetLoss}
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : parseFloat(e.target.value);
                                setNetworkQualityThresholds(prev => ({ ...prev, packetLoss: value }));
                              }}
                              sx={{ width: 120 }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                              Showing devices with packet loss &gt; {formatPacketLoss(threshold)} ({affectedDevices.length} devices)
                            </Typography>
                          </Box>
                          {affectedDevices.length > 0 ? (
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Device Type</TableCell>
                                    <TableCell sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Serial Number</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Transmission Failures (%)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {affectedDevices.map((device) => {
                                    const devicePacketLoss = device.backhaul_tx_failed_percentage;
                                    return (
                                      <TableRow key={device.serial_number} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>{getDeviceType(device.capabilities)}</TableCell>
                                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace', textAlign: 'left' }}>
                                          <Link
                                            component="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate(`/device/${device.serial_number?.toLowerCase() || device.serial_number}`);
                                            }}
                                            sx={{
                                              fontSize: '0.8rem',
                                              fontFamily: 'monospace',
                                              textDecoration: 'none',
                                              color: '#1976d2',
                                              cursor: 'pointer',
                                              background: 'none',
                                              border: 'none',
                                              padding: 0,
                                              textAlign: 'left',
                                              display: 'block',
                                              '&:hover': {
                                                textDecoration: 'underline',
                                                color: '#1565c0'
                                              }
                                            }}
                                          >
                                            {device.serial_number}
                                          </Link>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace', color: devicePacketLoss !== null && devicePacketLoss !== undefined ? getPacketLossColor(devicePacketLoss) : 'text.secondary' }}>
                                          {formatPacketLoss(devicePacketLoss)}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
                              No devices found with packet loss &gt; {formatPacketLoss(threshold)}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                );
              })()}
              {(minConnectedTimeHours !== null || avgConnectedTimeHours !== null || medianConnectedTimeHours !== null) && (
                <>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      display: 'block',
                      mt: 0.5,
                      mb: 0.5
                    }}>
                      Connected Time
                    </Typography>
                  </Box>
                  {minConnectedTimeHours !== null && (
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.875rem', color: 'text.secondary', display: 'block' }}>
                          Min Connected Time
                        </Typography>
                        <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                          {minConnectedTimeHours} h
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {avgConnectedTimeHours !== null && (() => {
                    const metricKey = 'connectedTimeAvg';
                    const isExpanded = expandedNetworkQuality[metricKey];
                    const thresholdSec = networkQualityThresholds.connectedTimeAvg !== null ? networkQualityThresholds.connectedTimeAvg : avgConnectedTimeSec;
                    const thresholdHours = thresholdSec !== null ? convertSecondsToHours(thresholdSec) : null;
                    const affectedDevices = getDevicesWithLowConnectedTime(thresholdSec);
                    
                    return (
                      <Box key={metricKey} sx={{ width: '100%' }}>
                        <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 0.5, position: 'relative' }}>
                          <Box 
                            sx={{ 
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5
                            }}
                            onClick={() => toggleNetworkQualityExpand(metricKey)}
                          >
                            <ChevronRightIcon 
                              sx={{ 
                                fontSize: 14,
                                color: 'text.secondary',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }} 
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.875rem', color: 'text.secondary', display: 'block' }}>
                              Avg Connected Time
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.25rem' }}>
                            {avgConnectedTimeHours} h
                          </Typography>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Threshold (hours)"
                                  value={thresholdHours !== null ? thresholdHours : ''}
                                  onChange={(e) => {
                                    const hours = e.target.value === '' ? null : parseFloat(e.target.value);
                                    const seconds = hours !== null ? hours * 3600 : null;
                                    setNetworkQualityThresholds(prev => ({ ...prev, connectedTimeAvg: seconds }));
                                  }}
                                  sx={{ width: 120 }}
                                  inputProps={{ min: 0, step: 0.1 }}
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                  Showing devices with connected time &lt; {thresholdHours} h ({affectedDevices.length} devices)
                                </Typography>
                              </Box>
                              {affectedDevices.length > 0 ? (
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Device Type</TableCell>
                                        <TableCell sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Serial Number</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Connected Time (h)</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {affectedDevices.map((device) => {
                                        const deviceConnectedTimeSec = getDeviceConnectedTime(device);
                                        const deviceConnectedTimeHours = deviceConnectedTimeSec !== null ? convertSecondsToHours(deviceConnectedTimeSec) : null;
                                        return (
                                          <TableRow key={device.serial_number} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                            <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>{getDeviceType(device.capabilities)}</TableCell>
                                            <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace', textAlign: 'left' }}>
                                              <Link
                                                component="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate(`/device/${device.serial_number?.toLowerCase() || device.serial_number}`);
                                                }}
                                                sx={{
                                                  fontSize: '0.8rem',
                                                  fontFamily: 'monospace',
                                                  textDecoration: 'none',
                                                  color: '#1976d2',
                                                  cursor: 'pointer',
                                                  background: 'none',
                                                  border: 'none',
                                                  padding: 0,
                                                  textAlign: 'left',
                                                  display: 'block',
                                                  '&:hover': {
                                                    textDecoration: 'underline',
                                                    color: '#1565c0'
                                                  }
                                                }}
                                              >
                                                {device.serial_number}
                                              </Link>
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                              {deviceConnectedTimeHours !== null ? deviceConnectedTimeHours : 'N/A'}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1, fontSize: '0.7rem' }}>
                                  No devices found with connected time &lt; {thresholdHours} h
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      </Box>
                    );
                  })()}
                  {medianConnectedTimeHours !== null && (() => {
                    const metricKey = 'connectedTimeMedian';
                    const isExpanded = expandedNetworkQuality[metricKey];
                    const thresholdSec = networkQualityThresholds.connectedTimeMedian !== null ? networkQualityThresholds.connectedTimeMedian : medianConnectedTimeSec;
                    const thresholdHours = thresholdSec !== null ? convertSecondsToHours(thresholdSec) : null;
                    const affectedDevices = getDevicesWithLowConnectedTime(thresholdSec);
                    
                    return (
                      <Box key={metricKey} sx={{ width: '100%' }}>
                        <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 0.5, position: 'relative' }}>
                          <Box 
                            sx={{ 
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5
                            }}
                            onClick={() => toggleNetworkQualityExpand(metricKey)}
                          >
                            <ChevronRightIcon 
                              sx={{ 
                                fontSize: 14,
                                color: 'text.secondary',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }} 
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.875rem', color: 'text.secondary', display: 'block' }}>
                              Median Connected Time
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.25rem' }}>
                            {medianConnectedTimeHours} h
                          </Typography>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Threshold (hours)"
                                  value={thresholdHours !== null ? thresholdHours : ''}
                                  onChange={(e) => {
                                    const hours = e.target.value === '' ? null : parseFloat(e.target.value);
                                    const seconds = hours !== null ? hours * 3600 : null;
                                    setNetworkQualityThresholds(prev => ({ ...prev, connectedTimeMedian: seconds }));
                                  }}
                                  sx={{ width: 120 }}
                                  inputProps={{ min: 0, step: 0.1 }}
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                  Showing devices with connected time &lt; {thresholdHours} h ({affectedDevices.length} devices)
                                </Typography>
                              </Box>
                              {affectedDevices.length > 0 ? (
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Device Type</TableCell>
                                        <TableCell sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Serial Number</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', py: 0.5 }}>Connected Time (h)</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {affectedDevices.map((device) => {
                                        const deviceConnectedTimeSec = getDeviceConnectedTime(device);
                                        const deviceConnectedTimeHours = deviceConnectedTimeSec !== null ? convertSecondsToHours(deviceConnectedTimeSec) : null;
                                        return (
                                          <TableRow key={device.serial_number} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                            <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>{getDeviceType(device.capabilities)}</TableCell>
                                            <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace', textAlign: 'left' }}>
                                              <Link
                                                component="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate(`/device/${device.serial_number?.toLowerCase() || device.serial_number}`);
                                                }}
                                                sx={{
                                                  fontSize: '0.8rem',
                                                  fontFamily: 'monospace',
                                                  textDecoration: 'none',
                                                  color: '#1976d2',
                                                  cursor: 'pointer',
                                                  background: 'none',
                                                  border: 'none',
                                                  padding: 0,
                                                  textAlign: 'left',
                                                  display: 'block',
                                                  '&:hover': {
                                                    textDecoration: 'underline',
                                                    color: '#1565c0'
                                                  }
                                                }}
                                              >
                                                {device.serial_number}
                                              </Link>
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                              {deviceConnectedTimeHours !== null ? deviceConnectedTimeHours : 'N/A'}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1, fontSize: '0.7rem' }}>
                                  No devices found with connected time &lt; {thresholdHours} h
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      </Box>
                    );
                  })()}
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Section 3: Software Versions Distribution - Full Width */}
      <Box sx={{ mt: 3, pt: 2 }}>
        <Typography variant="caption" sx={{ 
          display: 'block',
          mb: 1,
          fontSize: '0.7rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          color: 'text.secondary'
        }}>
          Software Versions Distribution
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Object.entries(softwareVersionsByType).map(([deviceType, versions]) => {
            const versionCount = typeof versions === 'object' && versions !== null
              ? Object.keys(versions).length
              : 0;
            const isExpanded = expandedVersions[deviceType];
            
            // Calculate total devices for this specific device type
            const deviceTypeData = sourceData[deviceType];
            let deviceTypeTotal = 0;
            if (deviceTypeData) {
              if (typeof deviceTypeData === 'object' && deviceTypeData !== null && !Array.isArray(deviceTypeData)) {
                const connected = typeof deviceTypeData.connected === 'number' ? deviceTypeData.connected : 0;
                const offline = typeof deviceTypeData.offline === 'number' ? deviceTypeData.offline : 0;
                deviceTypeTotal = connected + offline;
              } else if (typeof deviceTypeData === 'number') {
                deviceTypeTotal = deviceTypeData;
              }
            }
            
            return (
              <Paper
                key={deviceType}
                sx={{
                  borderRadius: 1,
                  border: 1,
                  borderColor: isExpanded ? `${JUGANU_YELLOW}66` : 'divider',
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: `${JUGANU_YELLOW}99` }
                }}
              >
                <Box
                  onClick={() => toggleVersionExpand(deviceType)}
                  sx={{
                    p: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 0.2s',
                    userSelect: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChevronRightIcon sx={{ 
                      fontSize: 18,
                      color: 'text.secondary',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {deviceType}
                    </Typography>
                    <Chip
                      label={`${versionCount} ver${versionCount !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: 'action.hover',
                        color: 'text.secondary',
                        '& .MuiChip-label': { px: 0.75 }
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.75rem' }}>
                    {deviceTypeTotal} devices
                  </Typography>
                </Box>
                <Collapse in={isExpanded}>
                  <Box sx={{ 
                    px: 1.5,
                    pb: 1,
                    pt: 0.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'action.hover'
                  }}>
                    {isExpanded && typeof versions === 'object' && versions !== null && (
                      <Grid container spacing={1}>
                        {Object.entries(versions)
                          .sort(([, a], [, b]) => b - a)
                          .map(([version, count]) => {
                            const percentage =
                              deviceTypeTotal > 0 ? ((count / deviceTypeTotal) * 100).toFixed(1) : '0.0';
                            
                            return (
                              <Grid item xs={12} sm={6} md={4} key={version}>
                                <Box sx={{
                                  p: 1,
                                  bgcolor: 'background.paper',
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 0.5,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '0.8rem' }}>
                                    {version}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.secondary' }}>
                                    {count} ({percentage}%)
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                      </Grid>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
          {Object.keys(softwareVersionsByType).length === 0 && (
            <Paper sx={{ p: 2, textAlign: 'center', color: 'text.secondary', borderRadius: 1 }}>
              No software version data available
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InstallationStatusTab;
